import Seo from "@/components/Seo";
import React, { useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { ALL_SLIDES, DECK } from "@/data/pitchContent";
import { SlideRenderer } from "@/pages/pitch/SlideRenderer";
import { SLIDE_W, SLIDE_H, DeckLogo } from "@/pages/pitch/SlideKit";

export default function PitchPrint() {
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0.8);
  const total = ALL_SLIDES.length;

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const compute = () => {
      const w = Math.min(el.getBoundingClientRect().width, 1120);
      setScale(w / SLIDE_W);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="pitch-print-root min-h-screen" style={{ background: DECK.grey }}>
      <Seo path="/pitch/print" title="WorkPay — Pitch Deck (PDF)" description="WorkPay investor pitch deck, print / PDF layout — the payment trust layer for service businesses. Confidential presentation." noindex />
      {/* Toolbar — hidden in print */}
      <div className="pitch-no-print sticky top-0 z-20 flex items-center justify-between px-6 h-16" style={{ background: "#fff", borderBottom: "1px solid rgba(11,11,15,0.08)" }}>
        <div className="flex items-center gap-4">
          <button data-testid="print-back" onClick={() => navigate("/pitch")} className="flex items-center gap-2 text-[14px] font-bold" style={{ color: DECK.muted }}>
            <ArrowLeft className="h-4 w-4" /> Back to deck
          </button>
          <span className="h-5 w-px" style={{ background: "rgba(11,11,15,0.12)" }} />
          <DeckLogo height={34} />
          <span className="text-[13px] font-semibold" style={{ color: DECK.muted }}>PDF export · {total} slides · 16:9</span>
        </div>
        <button data-testid="print-save-pdf" onClick={() => window.print()} className="flex items-center gap-2 rounded-full px-5 h-10 text-[14px] font-bold" style={{ background: DECK.ink, color: "#fff" }}>
          <Printer className="h-4 w-4" /> Save as PDF
        </button>
      </div>

      <div className="pitch-no-print text-center pt-6 px-6">
        <p className="text-[13px] font-medium" style={{ color: DECK.muted }}>
          Tip: in the print dialog choose “Save as PDF”, set margins to “None”, and enable “Background graphics”.
        </p>
      </div>

      {/* Slides */}
      <div ref={wrapRef} className="flex flex-col items-center gap-8 py-8 px-6">
        {ALL_SLIDES.map((sl, i) => (
          <div
            key={sl.id}
            className="pitch-print-page"
            style={{ width: SLIDE_W * scale, height: SLIDE_H * scale, background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 60px -30px rgba(11,11,15,0.4)" }}
          >
            <div className="pitch-print-canvas" style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${scale})`, transformOrigin: "top left" }}>
              <SlideRenderer slide={sl} index={i} total={total} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
